/* eslint-disable jsx-a11y/label-has-associated-control */
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';

import { IEventTicket } from '@/@types/events';
import { ReactComponent as IconWidgetSidebarCardSvg } from '@/assets/icon-widget-sidebar-cart.svg';
import Button from '@/components/Button';
import { useAuth } from '@/contexts/auth';
import { useCheckout } from '@/contexts/checkout';
import { api } from '@/services/api';
import { formattedMoney, showAlert } from '@/utils';
import { SeatingChart } from '@seatsio/seatsio-react';

import * as S from './styles';

const AuthModal = dynamic(() => import('@/components/AuthModal'), {
  ssr: false,
});

type Props = {
  type?: 'passive' | 'active' | 'semi-active';
  eventId: number;
  termChecked?: boolean;
  token?: string;
  tickets: IEventTicket[];
  selectedSeatsMapTickets?: { ticket: IEventTicket; objectId: string }[] | null;
  seatingChart?: SeatingChart;
  allTickets: IEventTicket[];
};

const Tickets = ({
  type = 'active',
  eventId,
  tickets,
  termChecked = false,
  token,
  selectedSeatsMapTickets,
  seatingChart,
  allTickets,
}: Props) => {
  const router = useRouter();
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [openModalAuth, setOpenModalAuth] = useState(false);
  const { isAuthenticated } = useAuth();
  const [mapTickets, setMapTickets] = useState<IEventTicket[]>([]);

  const {
    tickets: checkoutTickets,
    totalValueTax,
    totalValueOfTheTickets,
    addTicketToCheckout,
    totalValueInCheckout,
    totalValueInCart,
    findTotalSelectedItem,
    removeTicketFromCheckout,
  } = useCheckout();

  const purchaseIsValid = useCallback(() => {
    if (!isAuthenticated) {
      showAlert(
        'Para continuar a compra é necessário fazer login ou se cadastrar na plataforma',
        'error',
      );
      setOpenModalAuth(true);
      return false;
    }

    if (totalValueInCheckout <= 0) {
      showAlert('Selecione os ingressos.', 'error');
      return false;
    }

    if (!termChecked) {
      showAlert('É necessário aceitar os termos antes de prosseguir.', 'error');
      return false;
    }

    return true;
  }, [isAuthenticated, termChecked, totalValueInCheckout]);

  const handlePurchase = useCallback(async () => {
    if (purchaseIsValid()) {
      try {
        setLoadingPayment(true);

        await api.post(
          `/v1/web/events/${eventId}/tickets-available${
            token ? `?cToken=${token}` : ``
          }`,
          checkoutTickets.map(({ id, quantity }) => ({ id, quantity })),
        );

        if (window.fbq) {
          window.fbq('track', 'InitiateCheckout');
        }

        await router.push(
          `/checkout/${process.env.NEXT_PUBLIC_EQUIPMENT_KEY}/${eventId}${
            token ? `/${token}` : ``
          }`,
        );
      } finally {
        setLoadingPayment(false);
      }
    }
  }, [purchaseIsValid, eventId, checkoutTickets, router]);

  useEffect(() => {
    const filteredMapTickets = checkoutTickets.filter(
      item =>
        item.CATEGORIA_SEATIO_SETOR && item.CATEGORIA_SEATIO_SETOR.length > 0,
    );
    setMapTickets(filteredMapTickets);
  }, [checkoutTickets]);

  useEffect(() => {
    // seleciona todos os items do mapa no checkout
    if (selectedSeatsMapTickets && seatingChart) {
      const allMapTicketsInCheckout = checkoutTickets.filter(
        ticket =>
          !!selectedSeatsMapTickets.find(
            value => value.ticket.id === ticket.id,
          ),
      );

      // remove todos os itens do mapa do checkout
      allMapTicketsInCheckout.forEach(ticket => {
        Array.from(Array(ticket.quantity)).forEach(() => {
          removeTicketFromCheckout(ticket);
        });
      });

      // adiciona todos os itens, anteriores junto com os novos
      const mapTicketsToAddToCheckout = selectedSeatsMapTickets.filter(item => {
        if (
          !item.ticket.CATEGORIA_SEATIO_SETOR ||
          item.ticket.CATEGORIA_SEATIO_SETOR.length <= 0
        ) {
          return false;
        }
        return true;
      });

      // adiciona itens no checkout
      mapTicketsToAddToCheckout.forEach(item => {
        addTicketToCheckout(item.ticket, item.ticket.eventId);
      });

      const mapTicketsToRemoveFromCheckout = checkoutTickets.filter(
        item =>
          item.CATEGORIA_SEATIO_SETOR &&
          item.CATEGORIA_SEATIO_SETOR.length > 0 &&
          !selectedSeatsMapTickets.find(value => value.ticket.id === item.id),
      );

      const mapExtraTicketsToRemoveFromCheckout = checkoutTickets.filter(
        item =>
          item.RELACIONADOS_ITEM.length > 0 &&
          mapTicketsToRemoveFromCheckout.find(
            value => String(value.id) === item.RELACIONADOS_ITEM,
          ),
      );

      mapTicketsToRemoveFromCheckout.forEach(item => {
        removeTicketFromCheckout(item);
      });
      mapExtraTicketsToRemoveFromCheckout.forEach(item => {
        removeTicketFromCheckout(item);
      });
    }
  }, [selectedSeatsMapTickets]);

  const handleRemoveTicketFromCheckout = (ticket: IEventTicket) => {
    const extraTicketsToRemoveFromCheckout = checkoutTickets.filter(
      item =>
        item.RELACIONADOS_ITEM.length > 0 &&
        String(ticket.id) === item.RELACIONADOS_ITEM,
    );

    removeTicketFromCheckout(ticket);
    extraTicketsToRemoveFromCheckout.forEach(item => {
      removeTicketFromCheckout(item);
    });
  };

  return (
    <>
      <S.Header>
        <h2>Ingressos</h2>

        <S.HeaderTotal>
          <IconWidgetSidebarCardSvg />
          <p>{formattedMoney(totalValueInCheckout)}</p>
        </S.HeaderTotal>
      </S.Header>

      <S.TicketWrapper>
        {tickets
          .filter(row => row.RELACIONADOS_ITEM.length <= 0)
          .map(row =>
            type === 'passive' && findTotalSelectedItem(row) === 0 ? (
              ''
            ) : (
              <>
                <S.Ticket key={`ticket-by-event-id-${row.id}`}>
                  <header className="header">
                    <h3 className="title">{row.name}</h3>

                    {row.CASADINHA_ITEM === 'SIM' ? (
                      <p className="amount">
                        {formattedMoney(
                          Number(row.value) * Number(row.FATOR_CASADINHA_ITEM),
                        )}{' '}
                        {row.tax &&
                          `(+ ${formattedMoney(
                            Number(row.tax) * Number(row.FATOR_CASADINHA_ITEM),
                          )} taxa) (${row.FATOR_CASADINHA_ITEM} ingressos)`}
                      </p>
                    ) : (
                      <p className="amount">
                        {formattedMoney(row.value)}{' '}
                        {row.tax && `(+ ${formattedMoney(row.tax)} taxa)`}
                      </p>
                    )}
                  </header>

                  <div className="actions">
                    <button
                      type="button"
                      onClick={() => handleRemoveTicketFromCheckout(row)}
                      disabled={loadingPayment}
                      style={{
                        display: type === 'passive' ? 'none' : 'initial',
                      }}
                    >
                      <img
                        src="/images/minus.svg"
                        alt="Ícone que ao ser clicado retira um ingresso"
                      />
                    </button>

                    <p className="total">{findTotalSelectedItem(row)}</p>

                    <button
                      type="button"
                      onClick={() => addTicketToCheckout(row, row.eventId)}
                      disabled={loadingPayment}
                      style={{
                        display: type === 'passive' ? 'none' : 'initial',
                      }}
                    >
                      <img
                        src="/images/plus.svg"
                        alt="Ícone que ao ser clicado adiciona um ingresso"
                      />
                    </button>
                  </div>
                </S.Ticket>

                {allTickets
                  .filter(
                    item =>
                      item.RELACIONADOS_ITEM === String(row.id) &&
                      checkoutTickets.find(value => value.id === row.id),
                  )
                  .map(item => (
                    <S.Ticket key={`ticket-by-event-id-${item.id}`}>
                      <header className="header">
                        <h3 className="title">{item.name}</h3>

                        {item.CASADINHA_ITEM === 'SIM' ? (
                          <p className="amount">
                            {formattedMoney(
                              Number(item.value) *
                                Number(item.FATOR_CASADINHA_ITEM),
                            )}{' '}
                            {item.tax &&
                              `(+ ${formattedMoney(
                                Number(item.tax) *
                                  Number(item.FATOR_CASADINHA_ITEM),
                              )} taxa) (${
                                item.FATOR_CASADINHA_ITEM
                              } ingressos)`}
                          </p>
                        ) : (
                          <p className="amount">
                            {formattedMoney(item.value)}{' '}
                            {item.tax && `(+ ${formattedMoney(item.tax)} taxa)`}
                          </p>
                        )}

                        {/* <p className="installments">em até 4x R$ 47,90 </p>
                 <p className="expired-at">Vendas até 22/02/2020</p> */}
                      </header>

                      <div className="actions">
                        <button
                          type="button"
                          onClick={() => removeTicketFromCheckout(item)}
                          disabled={loadingPayment}
                          style={{
                            display: type === 'passive' ? 'none' : 'initial',
                          }}
                        >
                          <img
                            src="/images/minus.svg"
                            alt="Ícone que ao ser clicado retira um ingresso"
                          />
                        </button>

                        <p className="total">{findTotalSelectedItem(item)}</p>

                        <button
                          type="button"
                          onClick={() =>
                            addTicketToCheckout(item, item.eventId)
                          }
                          disabled={loadingPayment}
                          style={{
                            display: type === 'passive' ? 'none' : 'initial',
                          }}
                        >
                          <img
                            src="/images/plus.svg"
                            alt="Ícone que ao ser clicado adiciona um ingresso"
                          />
                        </button>
                      </div>
                    </S.Ticket>
                  ))}
              </>
            ),
          )}

        {mapTickets
          .filter(row => row.RELACIONADOS_ITEM.length <= 0)
          .map(row =>
            type === 'passive' && findTotalSelectedItem(row) === 0 ? (
              ''
            ) : (
              <>
                <S.Ticket key={`ticket-by-event-id-${row.id}`}>
                  <header className="header">
                    <h3 className="title">{row.name}</h3>

                    {row.CASADINHA_ITEM === 'SIM' ? (
                      <p className="amount">
                        {formattedMoney(
                          Number(row.value) * Number(row.FATOR_CASADINHA_ITEM),
                        )}{' '}
                        {row.tax &&
                          `(+ ${formattedMoney(
                            Number(row.tax) * Number(row.FATOR_CASADINHA_ITEM),
                          )} taxa) (${row.FATOR_CASADINHA_ITEM} ingressos)`}
                      </p>
                    ) : (
                      <p className="amount">
                        {formattedMoney(row.value)}{' '}
                        {row.tax && `(+ ${formattedMoney(row.tax)} taxa)`}
                      </p>
                    )}
                  </header>

                  <div className="actions">
                    <p className="total">{findTotalSelectedItem(row)}</p>
                  </div>
                </S.Ticket>

                {allTickets
                  .filter(item => item.RELACIONADOS_ITEM === String(row.id))
                  .map(item => (
                    <S.Ticket key={`ticket-by-event-id-${item.id}`}>
                      <header className="header">
                        <h3 className="title">{item.name}</h3>

                        {item.CASADINHA_ITEM === 'SIM' ? (
                          <p className="amount">
                            {formattedMoney(
                              Number(item.value) *
                                Number(item.FATOR_CASADINHA_ITEM),
                            )}{' '}
                            {item.tax &&
                              `(+ ${formattedMoney(
                                Number(item.tax) *
                                  Number(item.FATOR_CASADINHA_ITEM),
                              )} taxa) (${
                                item.FATOR_CASADINHA_ITEM
                              } ingressos)`}
                          </p>
                        ) : (
                          <p className="amount">
                            {formattedMoney(item.value)}{' '}
                            {item.tax && `(+ ${formattedMoney(item.tax)} taxa)`}
                          </p>
                        )}
                      </header>

                      <div className="actions">
                        <button
                          type="button"
                          onClick={() => removeTicketFromCheckout(item)}
                          disabled={loadingPayment}
                          style={{
                            display: type === 'passive' ? 'none' : 'initial',
                          }}
                        >
                          <img
                            src="/images/minus.svg"
                            alt="Ícone que ao ser clicado retira um ingresso"
                          />
                        </button>

                        <p className="total">{findTotalSelectedItem(item)}</p>

                        <button
                          type="button"
                          onClick={() =>
                            addTicketToCheckout(item, item.eventId)
                          }
                          disabled={loadingPayment}
                          style={{
                            display: type === 'passive' ? 'none' : 'initial',
                          }}
                        >
                          <img
                            src="/images/plus.svg"
                            alt="Ícone que ao ser clicado adiciona um ingresso"
                          />
                        </button>
                      </div>
                    </S.Ticket>
                  ))}
              </>
            ),
          )}
      </S.TicketWrapper>

      {/* <S.PromoCode status={type}>
        <label htmlFor="promo-ticket">Usar cupom promocional</label>
        <button type="button" id="promo-ticket">
          USAR
          <span>&gt;</span>
        </button>
      </S.PromoCode> */}

      <S.Amounts>
        <p>Valor Ingressos: {formattedMoney(totalValueOfTheTickets)}</p>
        <p>Desconto: {formattedMoney(0)}</p>
        <p>Taxas: {formattedMoney(totalValueTax)}</p>
        <p className="sub-total">
          <span>Sub-Total: {formattedMoney(totalValueInCart)}</span>
        </p>
      </S.Amounts>

      {(tickets.length > 0 || mapTickets.length > 0) && (
        <S.Footer status={type}>
          <Button
            type="button"
            onClick={handlePurchase}
            disabled={loadingPayment}
          >
            {loadingPayment ? 'Aguarde...' : 'Comprar Ingressos'}
          </Button>
        </S.Footer>
      )}

      <AuthModal open={openModalAuth} onClose={() => setOpenModalAuth(false)} />
    </>
  );
};

export default React.memo(Tickets);
